import type { CallBackProps, Step, TooltipRenderProps } from 'react-joyride';
import { Button, Checkbox, IconButton, Text } from '@ksyos/design-system';
import { useCallback, useEffect, useState } from 'react';
import Joyride, { ACTIONS, EVENTS, LIFECYCLE } from 'react-joyride';
import { styled } from 'styled-components';
import { translateKey } from '@frontend/translate';

interface CustomWindow extends Window {
    __webpack_nonce__: string;
}

interface IToolTipCheckboxProps {
    hasCheckbox: boolean;
    isChecked: boolean;
    onChange: (isChecked: boolean) => void;
}

export interface DecisionAidProps {
    isOpen: boolean;
    onClose: () => void;
    selectedStep?: string;
    steps: Step[];
    toolTipCheckboxProps: IToolTipCheckboxProps;
}

export type DecisionAidPlacement = 'top' | 'bottom';

export function DecisionAid({ isOpen = false, onClose, selectedStep, steps, toolTipCheckboxProps }: DecisionAidProps) {
    const [stepIndex, setStepIndex] = useState<number>(0);

    const handleJoyrideCallback = useCallback(
        (data: CallBackProps) => {
            const { action, index, lifecycle, type } = data;

            if (type === EVENTS.STEP_AFTER && lifecycle === LIFECYCLE.COMPLETE) {
                if (action === ACTIONS.NEXT) {
                    setStepIndex(index + 1);
                } else if (action === ACTIONS.PREV) {
                    setStepIndex(index - 1);
                } else if (action === ACTIONS.CLOSE) {
                    onClose();
                    setStepIndex(0);
                }
            }
        },
        [onClose]
    );

    useEffect(() => {
        if (selectedStep) {
            setStepIndex(getStepIndexByTitle(selectedStep));
        }
    }, [isOpen, selectedStep]);

    return (
        <Joyride
            callback={handleJoyrideCallback}
            continuous
            disableOverlayClose
            locale={{
                back: translateKey('common.previous'),
                last: translateKey('common.close'),
                next: translateKey('common.next'),
                close: translateKey('common.close'),
            }}
            nonce={(window as unknown as CustomWindow).__webpack_nonce__}
            run={isOpen}
            scrollOffset={72}
            scrollToFirstStep
            showProgress
            showSkipButton
            spotlightClicks
            spotlightPadding={4}
            stepIndex={stepIndex}
            steps={steps}
            styles={{ options: { zIndex: 900 } }}
            tooltipComponent={Tooltip(toolTipCheckboxProps)}
        />
    );

    function getStepIndexByTitle(title: string | undefined) {
        if (!title) {
            return 0;
        }

        return steps.findIndex((step) => step.title === title);
    }
}

const Tooltip =
    (toolTipCheckboxProps: IToolTipCheckboxProps) =>
    ({
        backProps,
        closeProps,
        continuous,
        index,
        isLastStep,
        primaryProps,
        size,
        step,
        tooltipProps,
    }: TooltipRenderProps) => {
        const { hasCheckbox, isChecked, onChange } = toolTipCheckboxProps;

        return (
            <StyledTooltip {...tooltipProps}>
                {step.title && (
                    <>
                        <Text
                            as="h3"
                            fontWeight="bold"
                            mb={4}
                            size="lg"
                        >
                            {step.title}
                        </Text>
                        <IconButton
                            icon="close"
                            labelText="close"
                            variant="default"
                            {...closeProps}
                            sx={{ position: 'absolute', top: 4, right: 4 }}
                        />
                    </>
                )}
                <StyledTooltipContent>{step.content}</StyledTooltipContent>
                <StyledTooltipFooter>
                    {hasCheckbox && (
                        <Checkbox
                            checked={isChecked}
                            label="Laat niet meer standaard zien"
                            onChange={(e) => onChange(e.target.checked)}
                            sx={{ flex: ['1 1 100%', '1 1 0'] }}
                        />
                    )}
                    {index > 0 && (
                        <Button
                            variant="link"
                            {...backProps}
                        >
                            {backProps.title}
                        </Button>
                    )}
                    {continuous && !isLastStep && (
                        <Button
                            variant="outline"
                            {...primaryProps}
                        >
                            {primaryProps.title} ({index + 1}/{size})
                        </Button>
                    )}
                    {isLastStep && (
                        <Button
                            variant="outline"
                            {...closeProps}
                        >
                            {closeProps.title}
                        </Button>
                    )}
                </StyledTooltipFooter>
            </StyledTooltip>
        );
    };

const StyledTooltip = styled.div`
    background-color: white;
    border-radius: 4px;
    max-width: 664px;
    padding: 24px 32px;
    position: relative;
`;

const StyledTooltipContent = styled.div``;

const StyledTooltipFooter = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: flex-end;
    margin-top: 32px;
    gap: 12px;
`;
